import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { joinClub } from 'helpers/customerio';
import * as styles from './JoinClubModal.module.css';

const JoinClubModal = ({ show, handleClose, handleSidebarClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: '+',
    pets: [],
    loading: false,
    success: false,
    error: false,
    step: 1
  });

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData(prevData => {
        const updatedPets = checked
          ? [...prevData.pets, value]
          : prevData.pets.filter(pet => pet !== value);
        return { ...prevData, pets: updatedPets };
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePhoneChange = e => {
    let { value } = e.target;

    // Remove all non-numeric characters except the leading '+'
    if (!value.startsWith('+')) {
      value = '+' + value.replace(/[^\d]/g, '');
    } else {
      value = value.charAt(0) + value.slice(1).replace(/[^\d]/g, '');
    }

    const cleaned = value.replace(/[^\d]/g, '');
    let formatted = `+${cleaned}`;

    // Apply Australian phone number formatting
    if (cleaned.startsWith('61')) {
      if (cleaned.length > 2 && cleaned.charAt(2) === '4') {
        // Mobile number
        const match = cleaned.match(/^(\d{2})(\d{1})(\d{3})(\d{3})(\d{0,3})$/);
        if (match) {
          formatted = `+${match[1]} ${match[2]} ${match[3]} ${match[4]}${
            match[5] ? ' ' + match[5] : ''
          }`;
        }
      } else {
        // Landline number
        const match = cleaned.match(/^(\d{2})(\d{1})(\d{4})(\d{4})$/);
        if (match) {
          formatted = `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
        }
      }
    } else {
      // Generic international number
      const match = cleaned.match(/^(\d{1,3})(\d{1,4})(\d{1,4})(\d{0,4})$/);
      if (match) {
        formatted = `+${match[1]} ${match[2]} ${match[3]}${match[4] ? ' ' + match[4] : ''}`;
      }
    }

    if (cleaned.startsWith('61') && cleaned.length > 11) {
      formatted = formatted.slice(0, 16); // For landline numbers
    } else if (cleaned.startsWith('61') && cleaned.length > 12) {
      formatted = formatted.slice(0, 16); // For mobile numbers
    } else if (formatted.length > 16) {
      // Generic international number length check
      formatted = formatted.slice(0, 16);
    }

    setFormData(prevData => ({ ...prevData, phone: formatted }));
  };

  const handleSubmitStep1 = async e => {
    e.preventDefault();
    setFormData(prevData => ({ ...prevData, loading: true }));

    const payload = {
      pet_chemist_club: 'pop-up',
      email: formData.email,
      first_name: formData.firstName,
      pets: JSON.stringify(formData.pets)
    };

    try {
      const response = await joinClub(payload);
      if (response.status === 0) {
        setFormData(prevData => ({ ...prevData, success: true, error: false, step: 2 }));
      } else {
        setFormData(prevData => ({ ...prevData, success: false, error: true }));
      }
    } catch (error) {
      setFormData(prevData => ({ ...prevData, success: false, error: true }));
    } finally {
      setFormData(prevData => ({ ...prevData, loading: false }));
    }
  };

  const handleSubmitStep2 = async e => {
    e.preventDefault();
    setFormData(prevData => ({ ...prevData, loading: true }));

    const payload = {
      pet_chemist_club: 'pop-up',
      email: formData.email,
      first_name: formData.firstName,
      phone: formData.phone,
      pets: JSON.stringify(formData.pets)
    };

    try {
      const response = await joinClub(payload);
      if (response.status === 0) {
        setFormData(prevData => ({ ...prevData, success: true, error: false, step: 3 }));
      } else {
        setFormData(prevData => ({ ...prevData, success: false, error: true }));
      }
    } catch (error) {
      setFormData(prevData => ({ ...prevData, success: false, error: true }));
    } finally {
      setFormData(prevData => ({ ...prevData, loading: false }));
    }
  };

  const handleShopNowClick = () => {
    handleClose();
    handleSidebarClose();
    window.location.href = '/special-offers';
  };

  return (
    <div className={`${styles.modal} ${show ? styles.show : styles.hide}`}>
      <div className={styles.joinClubContent}>
        <span className={styles.closeButton} onClick={handleClose}>
          &times;
        </span>
        {formData.success && formData.step === 3 ? (
          <div className={styles.successMessage}>
            <img
              src="https://d3k81ch9hvuctc.cloudfront.net/company/WnkPjx/images/620400c5-ab44-443a-b678-342ff750c886.png"
              alt="Thank you for joining!"
              className={styles.modalHeaderImage}
            />
            <img
              src="https://d3k81ch9hvuctc.cloudfront.net/company/WnkPjx/images/5d86fce6-4b2d-4dc9-854e-69bd46b710e0.png"
              alt="Success Image"
              className={styles.successImage}
            />
            <p className={styles.successText}>
              You'll receive an email with your coupon for $10 off your first Pet Chemist order,
              when you spend over $99
            </p>
            <button className={styles.shopNowButton} onClick={handleShopNowClick}>
              SHOP NOW
            </button>
          </div>
        ) : formData.error ? (
          <div className={styles.errorMessage}>
            Failed to join the club. Please try again.
            <button
              className={styles.retryButton}
              onClick={() => setFormData(prevData => ({ ...prevData, error: false }))}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <img
              src="https://d3k81ch9hvuctc.cloudfront.net/company/WnkPjx/images/0b3fb8dc-729d-4b0d-a19c-4a779ed9ff0a.png"
              alt="Join the Club for $10 Off"
              className={styles.modalHeaderImage}
            />
            {formData.step === 1 && (
              <form onSubmit={handleSubmitStep1}>
                <label className={styles.formLabel}>
                  First Name
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className={styles.textInput}
                  />
                </label>
                <label className={styles.formLabel}>
                  Email Address
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={styles.textInput}
                  />
                </label>
                <label className={styles.formLabel}>
                  What pet(s) do you have?
                  <div className={styles.checkboxGroup}>
                    {['Dog', 'Cat', 'Horse', 'Bird', 'Wildlife'].map(pet => (
                      <div key={pet} className={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          id={pet.toLowerCase()}
                          name="pets"
                          value={pet}
                          checked={formData.pets.includes(pet)}
                          onChange={handleInputChange}
                          className={styles.checkboxInput}
                        />
                        <label htmlFor={pet.toLowerCase()} className={styles.checkboxLabel}>
                          {pet}
                        </label>
                      </div>
                    ))}
                  </div>
                </label>
                <button type="submit" disabled={formData.loading} className={styles.submitButton}>
                  <span>{formData.loading ? 'SUBMITTING...' : 'JOIN CLUB'}</span>
                </button>
              </form>
            )}
            {formData.step === 2 && (
              <form onSubmit={handleSubmitStep2}>
                <p className={styles.dealsOffersText}>Get alerts on deals and offers</p>
                <label className={styles.formLabel}>
                  Phone Number
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    placeholder="+61 4xx xxx xxx"
                    className={styles.textInput}
                  />
                </label>
                <p className={styles.infoText}>
                  By entering your phone number and submitting this form, you consent to receive
                  marketing text messages (such as promotion codes and cart reminders) from Pet
                  Chemist at the number provided. Consent is not a condition of any purchase. You
                  can unsubscribe at any time by clicking the unsubscribe link (where available) in
                  one of our messages. Privacy Policy & Terms.
                </p>
                <button type="submit" disabled={formData.loading} className={styles.submitButton}>
                  <span>{formData.loading ? 'JOINING...' : 'JOIN CLUB'}</span>
                </button>
                <button
                  type="button"
                  className={styles.noThanksButton}
                  onClick={() => setFormData(prevData => ({ ...prevData, step: 3 }))}
                >
                  NO, THANKS GIVE ME MY $10
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

JoinClubModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSidebarClose: PropTypes.func.isRequired
};

export default JoinClubModal;
